import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { 
  Users, FileText, DollarSign, Eye, TrendingUp, Clock,
  CheckCircle, XCircle, Trash2, Shield, ArrowLeft, AlertTriangle, BarChart3
} from 'lucide-react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, AreaChart, Area } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPanel = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [listings, setListings] = useState([]);
  const [flaggedListings, setFlaggedListings] = useState([]);
  const [scamStats, setScamStats] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isAr = i18n.language === 'ar';

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { navigate('/auth'); return; }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      if (activeTab === 'dashboard') {
        const [statsRes, scamRes] = await Promise.all([
          axios.get(`${API}/admin/stats`, getAuthHeaders()),
          axios.get(`${API}/admin/scam-stats`, getAuthHeaders())
        ]);
        setStats(statsRes.data);
        setScamStats(scamRes.data);
      } else if (activeTab === 'users') {
        const res = await axios.get(`${API}/admin/users`, getAuthHeaders());
        setUsers(res.data);
      } else if (activeTab === 'listings') {
        const res = await axios.get(`${API}/admin/listings`, getAuthHeaders());
        setListings(res.data);
      } else if (activeTab === 'flagged') {
        const res = await axios.get(`${API}/admin/flagged-listings`, getAuthHeaders());
        setFlaggedListings(res.data);
      } else if (activeTab === 'analytics') {
        const res = await axios.get(`${API}/admin/analytics`, getAuthHeaders());
        setAnalyticsData(res.data);
      }
    } catch (err) {
      console.error('Error:', err);
      setError(err.response?.status === 403 ? 'admin_access' : 'load_failed');
    } finally {
      setLoading(false);
    }
  };

  const updateListingStatus = async (listingId, status) => {
    try {
      await axios.put(`${API}/admin/listings/${listingId}/status?status=${status}`, {}, getAuthHeaders());
      fetchData();
    } catch (err) { alert(t('admin.statusFailed')); }
  };

  const deleteListing = async (listingId) => {
    if (!window.confirm(t('admin.confirmDelete'))) return;
    try {
      await axios.delete(`${API}/admin/listings/${listingId}`, getAuthHeaders());
      fetchData();
    } catch (err) { alert(t('admin.deleteFailed')); }
  };

  const toggleAdmin = async (userId) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/toggle-admin`, {}, getAuthHeaders());
      fetchData();
    } catch (err) { alert(err.response?.data?.detail || t('admin.toggleAdminFailed')); }
  };

  const toggleBan = async (userId) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/toggle-ban`, {}, getAuthHeaders());
      fetchData();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to update user status'); }
  };

  const tabs = [
    { id: 'dashboard', labelKey: 'admin.dashboard', icon: TrendingUp },
    { id: 'analytics', labelKey: 'admin.analytics', icon: BarChart3 },
    { id: 'flagged', labelKey: 'admin.flagged', icon: AlertTriangle },
    { id: 'listings', labelKey: 'admin.listings', icon: FileText },
    { id: 'users', labelKey: 'admin.users', icon: Users },
  ];

  const chartTooltipStyle = {
    contentStyle: { background: '#1A1A2E', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', fontSize: '12px', color: '#fff' },
    itemStyle: { color: '#fff' },
    labelStyle: { color: 'rgba(255,255,255,0.5)' }
  };

  if (error === 'admin_access') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1A1A2E] via-[#16213E] to-[#0F3460] flex items-center justify-center pb-32">
        <div className="text-center animate-fade-up">
          <div className="w-20 h-20 bg-white/[0.05] rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Shield className="w-10 h-10 text-white/30" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2 font-heading">{t('admin.accessRequired')}</h2>
          <p className="text-white/40 text-sm mb-5">{t('admin.noPermission')}</p>
          <button onClick={() => navigate('/')}
            className="px-6 py-3 bg-[#E31C25] text-white rounded-xl font-bold text-sm shadow-lg shadow-[#E31C25]/20">
            {t('admin.goHome')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1A1A2E] via-[#16213E] to-[#0F3460] pb-40 md:pb-8">
      {/* Dark Hero Header */}
      <div className="hero-gradient relative">
        <div className="noise-overlay" />
        <div className="max-w-6xl mx-auto px-4 pt-4 pb-5 relative z-10">
          <div className="flex items-center justify-between mb-5">
            <button onClick={() => navigate(-1)} className="w-10 h-10 rounded-xl bg-white/8 backdrop-blur-sm flex items-center justify-center hover:bg-white/15 transition-all active:scale-90" data-testid="admin-back">
              <ArrowLeft className="w-5 h-5 text-white" />
            </button>
            <div className="w-10" />
          </div>
          <div className="animate-fade-up">
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 bg-[#E31C25]/15 rounded-xl flex items-center justify-center">
                <Shield className="w-5 h-5 text-[#E31C25]" />
              </div>
              <h1 className="text-2xl font-extrabold text-white font-heading tracking-tight">{t('admin.title')}</h1>
            </div>
            <p className="text-white/40 text-sm mt-1">{isAr ? 'إدارة المنصة والمستخدمين' : 'Manage platform & users'}</p>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mt-5 overflow-x-auto category-scroll pb-1">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap transition-all ${
                    activeTab === tab.id
                      ? 'bg-[#E31C25] text-white shadow-lg shadow-[#E31C25]/25'
                      : 'bg-white/8 text-white/50 hover:bg-white/12'
                  }`}
                  data-testid={`tab-${tab.id}`}>
                  <Icon className="w-4 h-4" />
                  {t(tab.labelKey)}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 pt-5">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-9 h-9 border-[3px] border-[#E31C25] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* ========== DASHBOARD TAB ========== */}
            {activeTab === 'dashboard' && stats && (
              <div className="space-y-5">
                {/* Today's Activity Banner */}
                <div className="bg-gradient-to-r from-[#E31C25] to-[#b91420] rounded-2xl p-5 relative overflow-hidden animate-fade-up" data-testid="today-activity">
                  <div className="absolute -top-6 -right-6 w-28 h-28 bg-white/10 rounded-full" />
                  <div className="absolute -bottom-4 -left-4 w-20 h-20 bg-white/5 rounded-full" />
                  <h3 className="font-bold text-white/80 text-xs uppercase tracking-wider mb-3 relative z-10">{t('admin.todayActivity')}</h3>
                  <div className="grid grid-cols-2 gap-4 relative z-10">
                    <div>
                      <p className="text-4xl font-extrabold text-white">{stats.new_users_today || 0}</p>
                      <p className="text-sm text-white/60 font-medium">{t('admin.newUsers')}</p>
                    </div>
                    <div>
                      <p className="text-4xl font-extrabold text-white">{stats.new_listings_today || 0}</p>
                      <p className="text-sm text-white/60 font-medium">{t('admin.newListings')}</p>
                    </div>
                  </div>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {[
                    { val: stats.total_users, label: t('admin.totalUsers'), icon: Users, color: 'blue' },
                    { val: stats.total_listings, label: t('admin.totalListings'), icon: FileText, color: 'emerald' },
                    { val: stats.active_listings, label: t('admin.activeListings'), icon: CheckCircle, color: 'green' },
                    { val: stats.pending_listings, label: t('admin.pendingReview'), icon: Clock, color: 'amber' },
                  ].map((s, i) => {
                    const Icon = s.icon;
                    const colors = { blue: 'bg-blue-500/15 text-blue-400', emerald: 'bg-emerald-500/15 text-emerald-400', green: 'bg-green-500/15 text-green-400', amber: 'bg-amber-500/15 text-amber-400' };
                    return (
                      <div key={i} className={`animate-fade-up stagger-${i + 1} bg-white/[0.03] backdrop-blur-sm rounded-2xl border border-white/[0.06] p-4`} data-testid={`stat-${i}`}>
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${colors[s.color]}`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <p className="text-3xl font-extrabold text-white">{s.val}</p>
                        <p className="text-xs text-white/40 font-medium mt-0.5">{s.label}</p>
                      </div>
                    );
                  })}
                  <div className="animate-fade-up stagger-5 bg-white/[0.03] backdrop-blur-sm rounded-2xl border border-white/[0.06] p-4 col-span-2 md:col-span-2" data-testid="stat-revenue">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3 bg-purple-500/15 text-purple-400">
                      <DollarSign className="w-5 h-5" />
                    </div>
                    <p className="text-3xl font-extrabold text-white">{t('common.aed')} {(stats.total_revenue || 0).toLocaleString()}</p>
                    <p className="text-xs text-white/40 font-medium mt-0.5">{t('admin.totalRevenue')}</p>
                  </div>
                </div>

                {/* Scam Detection Charts */}
                {scamStats && (
                  <div className="space-y-4 animate-fade-up stagger-6">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 bg-[#E31C25]/15 rounded-lg flex items-center justify-center">
                        <Shield className="w-4 h-4 text-[#E31C25]" />
                      </div>
                      <h3 className="font-bold text-white text-sm">{isAr ? 'إحصائيات كشف الاحتيال' : 'Scam Detection Stats'}</h3>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {/* Pie Chart */}
                      <div className="bg-white/[0.03] backdrop-blur-sm rounded-2xl border border-white/[0.06] p-5" data-testid="scam-pie-chart">
                        <p className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-4">{isAr ? 'توزيع الإعلانات' : 'Listing Health'}</p>
                        {scamStats.overview.total > 0 ? (
                          <ResponsiveContainer width="100%" height={200}>
                            <PieChart>
                              <Pie
                                data={[
                                  { name: isAr ? 'نظيف' : 'Clean', value: scamStats.overview.clean },
                                  { name: isAr ? 'مراجعة' : 'Flagged', value: scamStats.overview.flagged },
                                  { name: isAr ? 'مرفوض' : 'Rejected', value: scamStats.overview.rejected }
                                ].filter(d => d.value > 0)}
                                cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={3} dataKey="value">
                                <Cell fill="#22c55e" />
                                <Cell fill="#f59e0b" />
                                <Cell fill="#ef4444" />
                              </Pie>
                              <Tooltip {...chartTooltipStyle} />
                              <Legend wrapperStyle={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)' }} />
                            </PieChart>
                          </ResponsiveContainer>
                        ) : (
                          <div className="flex items-center justify-center h-[200px] text-white/25 text-sm">{isAr ? 'لا توجد بيانات' : 'No data yet'}</div>
                        )}
                      </div>

                      {/* Bar Chart */}
                      <div className="bg-white/[0.03] backdrop-blur-sm rounded-2xl border border-white/[0.06] p-5" data-testid="scam-bar-chart">
                        <p className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-4">{isAr ? 'النشاط اليومي (30 يوم)' : 'Daily Activity (30 days)'}</p>
                        {scamStats.daily.length > 0 ? (
                          <ResponsiveContainer width="100%" height={200}>
                            <BarChart data={scamStats.daily} barGap={0} barCategoryGap="20%">
                              <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'rgba(255,255,255,0.3)' }} axisLine={{ stroke: 'rgba(255,255,255,0.06)' }} tickLine={false} interval="preserveStartEnd" />
                              <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: 'rgba(255,255,255,0.3)' }} axisLine={false} tickLine={false} width={30} />
                              <Tooltip {...chartTooltipStyle} />
                              <Bar dataKey="clean" stackId="a" fill="#22c55e" name={isAr ? 'نظيف' : 'Clean'} />
                              <Bar dataKey="flagged" stackId="a" fill="#f59e0b" name={isAr ? 'مراجعة' : 'Flagged'} />
                              <Bar dataKey="rejected" stackId="a" fill="#ef4444" name={isAr ? 'مرفوض' : 'Rejected'} radius={[3, 3, 0, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        ) : (
                          <div className="flex items-center justify-center h-[200px] text-white/25 text-sm">{isAr ? 'لا توجد بيانات' : 'No data yet'}</div>
                        )}
                      </div>
                    </div>

                    {/* Top Triggered Rules */}
                    {scamStats.top_rules.length > 0 && (
                      <div className="bg-white/[0.03] backdrop-blur-sm rounded-2xl border border-white/[0.06] p-5" data-testid="scam-top-rules">
                        <p className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-4">{isAr ? 'أكثر القواعد تفعيلاً' : 'Top Triggered Rules'}</p>
                        <div className="space-y-3">
                          {scamStats.top_rules.map((rule, idx) => {
                            const maxCount = scamStats.top_rules[0]?.count || 1;
                            const ruleLabels = {
                              'scam_keyword': 'Scam Keywords', 'scam_keyword_ar': 'Scam Keywords (AR)',
                              'urgency_tactic': 'Urgency Tactics', 'urgency_tactic_ar': 'Urgency Tactics (AR)',
                              'excessive_contact_info': 'Excessive Contact', 'email_in_listing': 'Email in Listing',
                              'url_in_listing': 'URL in Listing', 'price_too_low': 'Low Price',
                              'price_too_high': 'High Price', 'all_caps_title': 'ALL CAPS Title',
                              'all_caps_description': 'ALL CAPS Desc', 'excessive_punctuation': 'Excess Punctuation',
                              'short_description': 'Short Description', 'repeated_characters': 'Repeated Chars',
                              'negative_price': 'Negative Price',
                            };
                            return (
                              <div key={idx} className="flex items-center gap-3">
                                <span className="text-xs text-white/50 w-36 truncate font-medium">{ruleLabels[rule.rule] || rule.rule}</span>
                                <div className="flex-1 bg-white/[0.06] rounded-full h-2 overflow-hidden">
                                  <div className="h-full rounded-full bg-gradient-to-r from-[#E31C25] to-[#f87171]" style={{ width: `${(rule.count / maxCount) * 100}%` }} />
                                </div>
                                <span className="text-xs font-bold text-white/60 w-8 text-right">{rule.count}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* ========== ANALYTICS TAB ========== */}
            {activeTab === 'analytics' && (
              <div className="space-y-4">
                {!analyticsData ? (
                  <div className="text-center py-16 text-white/30">{isAr ? 'جاري التحميل...' : 'Loading...'}</div>
                ) : (
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {[
                        { val: analyticsData.overview.total_views || 0, label: isAr ? 'إجمالي المشاهدات' : 'Total Views (30d)', icon: Eye, color: 'blue' },
                        { val: analyticsData.overview.unique_visitors || 0, label: isAr ? 'زوار فريدون' : 'Unique Visitors (30d)', icon: Users, color: 'green' },
                        { val: analyticsData.overview.views_today || 0, label: isAr ? 'مشاهدات اليوم' : 'Views Today', icon: TrendingUp, color: 'amber' },
                        { val: analyticsData.overview.visitors_today || 0, label: isAr ? 'زوار اليوم' : 'Visitors Today', icon: Users, color: 'purple' },
                      ].map((s, i) => {
                        const Icon = s.icon;
                        const colors = { blue: 'bg-blue-500/15 text-blue-400', green: 'bg-green-500/15 text-green-400', amber: 'bg-amber-500/15 text-amber-400', purple: 'bg-purple-500/15 text-purple-400' };
                        return (
                          <div key={i} className={`animate-fade-up stagger-${i + 1} bg-white/[0.03] backdrop-blur-sm rounded-2xl border border-white/[0.06] p-4`} data-testid={`analytics-${i}`}>
                            <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-2.5 ${colors[s.color]}`}>
                              <Icon className="w-4 h-4" />
                            </div>
                            <p className="text-2xl font-extrabold text-white">{s.val.toLocaleString()}</p>
                            <p className="text-[10px] text-white/35 font-medium mt-0.5">{s.label}</p>
                          </div>
                        );
                      })}
                    </div>

                    {/* Daily Traffic Chart */}
                    <div className="bg-white/[0.03] backdrop-blur-sm rounded-2xl border border-white/[0.06] p-5 animate-fade-up stagger-5" data-testid="analytics-daily-chart">
                      <p className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-4">{isAr ? 'حركة المرور اليومية' : 'Daily Traffic'}</p>
                      {analyticsData.daily.length > 0 ? (
                        <ResponsiveContainer width="100%" height={220}>
                          <AreaChart data={analyticsData.daily}>
                            <defs>
                              <linearGradient id="viewsGrad" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                              </linearGradient>
                              <linearGradient id="visitorsGrad" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
                                <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                              </linearGradient>
                            </defs>
                            <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'rgba(255,255,255,0.3)' }} axisLine={{ stroke: 'rgba(255,255,255,0.06)' }} tickLine={false} interval="preserveStartEnd" />
                            <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: 'rgba(255,255,255,0.3)' }} axisLine={false} tickLine={false} width={30} />
                            <Tooltip {...chartTooltipStyle} />
                            <Area type="monotone" dataKey="views" stroke="#3b82f6" fill="url(#viewsGrad)" name={isAr ? 'مشاهدات' : 'Views'} strokeWidth={2} />
                            <Area type="monotone" dataKey="visitors" stroke="#22c55e" fill="url(#visitorsGrad)" name={isAr ? 'زوار' : 'Visitors'} strokeWidth={2} />
                          </AreaChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="flex items-center justify-center h-[220px] text-white/25 text-sm">{isAr ? 'لا توجد بيانات بعد' : 'No traffic data yet.'}</div>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {/* Popular Pages */}
                      <div className="bg-white/[0.03] backdrop-blur-sm rounded-2xl border border-white/[0.06] p-5 animate-fade-up stagger-6" data-testid="analytics-popular-pages">
                        <p className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-4">{isAr ? 'الصفحات الأكثر زيارة' : 'Popular Pages'}</p>
                        {analyticsData.pages.length > 0 ? (
                          <div className="space-y-3">
                            {analyticsData.pages.map((p, idx) => {
                              const maxViews = analyticsData.pages[0]?.views || 1;
                              const pageLabels = { 'home': 'Homepage', 'search': 'Search', 'listing': 'Listing Detail', 'post-ad': 'Post Ad', 'auth': 'Login/Register' };
                              return (
                                <div key={idx} className="flex items-center gap-3">
                                  <span className="text-xs text-white/50 w-28 truncate font-medium">{pageLabels[p.page] || p.page}</span>
                                  <div className="flex-1 bg-white/[0.06] rounded-full h-2 overflow-hidden">
                                    <div className="h-full rounded-full bg-gradient-to-r from-blue-500 to-blue-400" style={{ width: `${(p.views / maxViews) * 100}%` }} />
                                  </div>
                                  <span className="text-xs font-bold text-white/60 w-12 text-right">{p.views}</span>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="text-white/25 text-sm text-center py-6">{isAr ? 'لا توجد بيانات' : 'No data yet'}</p>
                        )}
                      </div>

                      {/* Top Viewed Listings */}
                      <div className="bg-white/[0.03] backdrop-blur-sm rounded-2xl border border-white/[0.06] p-5 animate-fade-up stagger-7" data-testid="analytics-top-listings">
                        <p className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-4">{isAr ? 'الإعلانات الأكثر مشاهدة' : 'Top Viewed Listings'}</p>
                        {analyticsData.top_listings.length > 0 ? (
                          <div className="space-y-2.5">
                            {analyticsData.top_listings.map((l, idx) => (
                              <div key={idx} className="flex items-center justify-between gap-2 py-2 border-b border-white/[0.04] last:border-0">
                                <span className="text-sm text-white/70 truncate flex-1">{l.title}</span>
                                <span className="text-[10px] font-bold text-blue-400 bg-blue-500/15 px-2 py-0.5 rounded-full shrink-0">
                                  {l.views} {isAr ? 'مشاهدة' : 'views'}
                                </span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-white/25 text-sm text-center py-6">{isAr ? 'لا توجد بيانات' : 'No data yet'}</p>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* ========== LISTINGS TAB ========== */}
            {activeTab === 'listings' && (
              <div className="space-y-2.5">
                {listings.length === 0 ? (
                  <p className="text-center text-white/30 py-12">{t('admin.noListings')}</p>
                ) : listings.map((listing, i) => (
                  <div key={listing.id} className={`animate-fade-up stagger-${Math.min(i + 1, 9)} bg-white/[0.03] backdrop-blur-sm rounded-2xl border border-white/[0.06] p-4`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-white text-sm truncate">{listing.title}</h3>
                        <p className="text-xs text-white/35 mt-0.5">{listing.category_name} · {listing.location_name}</p>
                        <p className="text-base font-extrabold text-[#E31C25] mt-1">{t('common.aed')} {listing.price.toLocaleString()}</p>
                        <div className="flex items-center gap-3 mt-2 text-[10px] text-white/30">
                          <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> {listing.views_count}</span>
                          <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(listing.created_at).toLocaleDateString(isAr ? 'ar-AE' : 'en-AE')}</span>
                          {listing.scam_score > 0 && (
                            <span className="flex items-center gap-1 text-amber-400 font-medium">
                              <AlertTriangle className="w-3 h-3" /> {listing.scam_score}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2 shrink-0">
                        <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold ${
                          listing.status === 'active' ? 'bg-green-500/15 text-green-400' :
                          listing.status === 'pending' ? 'bg-amber-500/15 text-amber-400' :
                          listing.status === 'rejected' ? 'bg-red-500/15 text-red-400' :
                          listing.status === 'sold' ? 'bg-blue-500/15 text-blue-400' :
                          'bg-white/10 text-white/50'
                        }`}>
                          {listing.status === 'active' ? t('admin.active') : listing.status === 'pending' ? t('admin.pending') : listing.status === 'rejected' ? t('admin.rejected') : listing.status === 'sold' ? t('admin.sold') : listing.status}
                        </span>
                        <div className="flex gap-1">
                          {listing.status !== 'active' && (
                            <button onClick={() => updateListingStatus(listing.id, 'active')} className="p-1.5 text-green-400 hover:bg-green-500/15 rounded-lg transition-colors" title={t('admin.approve')}>
                              <CheckCircle className="w-4 h-4" />
                            </button>
                          )}
                          {listing.status !== 'rejected' && (
                            <button onClick={() => updateListingStatus(listing.id, 'rejected')} className="p-1.5 text-red-400 hover:bg-red-500/15 rounded-lg transition-colors" title={t('admin.reject')}>
                              <XCircle className="w-4 h-4" />
                            </button>
                          )}
                          <button onClick={() => deleteListing(listing.id)} className="p-1.5 text-white/30 hover:bg-white/10 rounded-lg transition-colors" title={t('admin.delete')}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* ========== FLAGGED TAB ========== */}
            {activeTab === 'flagged' && (
              <div className="space-y-2.5">
                {flaggedListings.length === 0 ? (
                  <div className="text-center py-16 animate-fade-up">
                    <div className="w-16 h-16 bg-green-500/15 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-8 h-8 text-green-400" />
                    </div>
                    <p className="text-white font-bold text-sm">{isAr ? 'لا توجد إعلانات مشبوهة' : 'No flagged listings'}</p>
                    <p className="text-white/30 text-xs mt-1">{isAr ? 'جميع الإعلانات نظيفة' : 'All listings look clean'}</p>
                  </div>
                ) : flaggedListings.map((listing, i) => {
                  let flags = [];
                  try { flags = JSON.parse(listing.scam_flags || '[]'); } catch (e) { /* ignore */ }
                  return (
                    <div key={listing.id}
                      className={`animate-fade-up stagger-${Math.min(i + 1, 9)} bg-white/[0.03] backdrop-blur-sm rounded-2xl border p-4 ${
                        listing.scam_score >= 50 ? 'border-red-500/20' : 'border-amber-500/20'
                      }`} data-testid={`flagged-listing-${listing.id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <h3 className="font-bold text-white text-sm truncate">{listing.title}</h3>
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                              listing.scam_score >= 50 ? 'bg-red-500/15 text-red-400' : 'bg-amber-500/15 text-amber-400'
                            }`} data-testid={`scam-score-${listing.id}`}>
                              {isAr ? 'نقاط' : 'Score'}: {listing.scam_score}
                            </span>
                          </div>
                          <p className="text-xs text-white/35">{listing.category_name} · {listing.location_name} · {listing.user_email}</p>
                          <p className="text-base font-extrabold text-[#E31C25] mt-1">{t('common.aed')} {listing.price.toLocaleString()}</p>
                          {flags.length > 0 && (
                            <div className="mt-2.5 flex flex-wrap gap-1.5">
                              {flags.map((flag, idx) => (
                                <span key={idx}
                                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${
                                    flag.severity >= 25 ? 'bg-red-500/15 text-red-400' :
                                    flag.severity >= 15 ? 'bg-orange-500/15 text-orange-400' :
                                    'bg-amber-500/15 text-amber-400'
                                  }`} data-testid={`flag-${listing.id}-${idx}`}>
                                  <AlertTriangle className="w-3 h-3" />{flag.detail}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col items-end gap-2 shrink-0">
                          <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold ${
                            listing.status === 'rejected' ? 'bg-red-500/15 text-red-400' :
                            listing.status === 'pending' ? 'bg-amber-500/15 text-amber-400' :
                            listing.status === 'active' ? 'bg-green-500/15 text-green-400' :
                            'bg-white/10 text-white/50'
                          }`}>
                            {listing.status === 'rejected' ? (isAr ? 'مرفوض' : 'Rejected') : listing.status === 'pending' ? (isAr ? 'مراجعة' : 'Review') : listing.status}
                          </span>
                          <div className="flex gap-1">
                            <button onClick={() => updateListingStatus(listing.id, 'active')} className="p-1.5 text-green-400 hover:bg-green-500/15 rounded-lg transition-colors" data-testid={`approve-flagged-${listing.id}`}>
                              <CheckCircle className="w-4 h-4" />
                            </button>
                            <button onClick={() => updateListingStatus(listing.id, 'rejected')} className="p-1.5 text-red-400 hover:bg-red-500/15 rounded-lg transition-colors" data-testid={`reject-flagged-${listing.id}`}>
                              <XCircle className="w-4 h-4" />
                            </button>
                            <button onClick={() => deleteListing(listing.id)} className="p-1.5 text-white/30 hover:bg-white/10 rounded-lg transition-colors" data-testid={`delete-flagged-${listing.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* ========== USERS TAB ========== */}
            {activeTab === 'users' && (
              <div className="space-y-2.5">
                {users.length === 0 ? (
                  <p className="text-center text-white/30 py-12">{t('admin.noUsers')}</p>
                ) : users.map((user, i) => (
                  <div key={user.id}
                    className={`animate-fade-up stagger-${Math.min(i + 1, 9)} bg-white/[0.03] backdrop-blur-sm rounded-2xl border p-4 ${
                      !user.is_active ? 'border-red-500/20' : 'border-white/[0.06]'
                    }`} data-testid={`admin-user-${user.id}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-bold text-white text-sm">{user.full_name || t('admin.noName')}</h3>
                          {user.is_admin && (
                            <span className="px-2 py-0.5 bg-purple-500/15 text-purple-400 text-[10px] font-bold rounded-full">{t('admin.admin')}</span>
                          )}
                          {!user.is_active && (
                            <span className="px-2 py-0.5 bg-red-500/15 text-red-400 text-[10px] font-bold rounded-full" data-testid="banned-badge">{isAr ? 'محظور' : 'Banned'}</span>
                          )}
                        </div>
                        <p className="text-xs text-white/35 mt-0.5">{user.email}</p>
                        <p className="text-[10px] text-white/25 mt-1">
                          {user.phone && <span>{user.phone} · </span>}
                          {user.listings_count} {t('admin.listings')} · {t('menu.joined')} {new Date(user.created_at).toLocaleDateString(isAr ? 'ar-AE' : 'en-AE')}
                        </p>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <button onClick={() => toggleBan(user.id)}
                          className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all active:scale-95 ${
                            user.is_active ? 'bg-red-500/15 text-red-400 hover:bg-red-500/25' : 'bg-green-500/15 text-green-400 hover:bg-green-500/25'
                          }`} data-testid={`ban-btn-${user.id}`}>
                          {user.is_active ? (isAr ? 'حظر' : 'Ban') : (isAr ? 'إلغاء' : 'Unban')}
                        </button>
                        <button onClick={() => toggleAdmin(user.id)}
                          className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all active:scale-95 ${
                            user.is_admin ? 'bg-white/10 text-white/50 hover:bg-white/15' : 'bg-purple-500/15 text-purple-400 hover:bg-purple-500/25'
                          }`} data-testid={`admin-btn-${user.id}`}>
                          {user.is_admin ? t('admin.removeAdmin') : t('admin.makeAdmin')}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;
